import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import PageHeader from "../../Component/PageHeader";
import StatStrip, { StatItem as StripItem } from "../../Component/StatStrip";
import PeriodFilter, { periodRange } from "../../Component/PeriodFilter";
import Pill from "../../Component/Pill";
import Loader from "../../Component/Loader/Loader";
import InvoiceDrawer from "../Invoices/components/InvoiceDrawer";
import { getRequest } from "../../utils/api";
import { logout } from "../../utils/auth";
import { ApiInvoice, customerName, inr2, inrShort, invoiceNo, invoiceStatus, invoiceTotals } from "../Invoices/invoiceUtils";

// Interface for Stats (value + growth % vs previous period)
interface StatItem {
  value: number;
  growth: number;
}

interface WalletStats {
  credited: StatItem;
  collected: StatItem;
  gst: StatItem;
  count: StatItem;
  customers: StatItem;
}

// Customer dropdown row from GET /subscription/walletTopups
interface WalletCustomer {
  _id: string;
  topups: number;
  user_unique_id?: string;
  user_companyName?: string;
  user_first_name?: string;
  user_last_name?: string;
  user_wallet_balance?: number;
}

const EMPTY_STAT: StatItem = { value: 0, growth: 0 };

const customerLabel = (c: WalletCustomer) =>
  c.user_companyName || `${c.user_first_name || ""} ${c.user_last_name || ""}`.trim() || c.user_unique_id || "—";

// Only invoice_plan_type = 'wallet_topup' invoices are shown here
const Wallet = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [topupList, setTopupList] = useState<ApiInvoice[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalTopups, setTotalTopups] = useState<number>(0);
  const [isLoader, setIsLoader] = useState<boolean>(true);

  // Stats State
  const [stats, setStats] = useState<WalletStats>({
    credited: EMPTY_STAT,
    collected: EMPTY_STAT,
    gst: EMPTY_STAT,
    count: EMPTY_STAT,
    customers: EMPTY_STAT,
  });
  const [customers, setCustomers] = useState<WalletCustomer[]>([]);
  const [methods, setMethods] = useState<string[]>([]);

  // Filters (?client=<user id> opens one customer's statement)
  const client = searchParams.get("client") || "all";
  const [period, setPeriod] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");

  const [openInvoice, setOpenInvoice] = useState<ApiInvoice | null>(null);

  const selectClient = (id: string) => {
    if (id === "all") searchParams.delete("client");
    else searchParams.set("client", id);
    setSearchParams(searchParams, { replace: true });
    setPage(1);
  };

  // Fetch Wallet Top-ups (list + stats follow the selected period and customer)
  const getTopupList = useCallback(async () => {
    // All time = no dates; custom waits until both dates are picked
    const [from, to] = periodRange(period, customFrom, customTo);
    if (period === "custom" && (!from || !to)) return;
    const startDate = from ? from.format('YYYY-MM-DD') : "";
    const endDate = to ? to.format('YYYY-MM-DD') : "";
    try {
      setIsLoader(true);
      const response = await getRequest(
        `/subscription/walletTopups?page=${page}&limit=${pageSize}&userId=${client}&invoiceStatus=${statusFilter}&method=${encodeURIComponent(methodFilter)}&search=${encodeURIComponent(searchQuery)}&sort=${sortOrder}&startDate=${startDate}&endDate=${endDate}`
      );

      if (response.status === 'success') {
        setTopupList(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotalTopups(response.pagination.total);
        if (response.stats) setStats(response.stats);
        if (response.customers) setCustomers(response.customers);
        if (response.methods) setMethods(response.methods);
      } else if (response.status === 'session_expired') {
        logout();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Something went wrong loading wallet top-ups!");
    } finally {
      setIsLoader(false);
    }
  }, [page, pageSize, client, statusFilter, methodFilter, searchQuery, sortOrder, period, customFrom, customTo]);

  useEffect(() => {
    getTopupList();
  }, [getTopupList]);

  // Debounced Search
  useEffect(() => {
    const delay = setTimeout(() => {
      setSearchQuery(searchText);
      setPage(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchText]);

  const one = client !== "all" ? customers.find(c => c._id === client) : undefined;

  // Export the rows currently loaded in the table as CSV
  const exportCsv = () => {
    if (!topupList.length) return toast.warning("Nothing to export");
    const head = ["Date", "Invoice", "Customer", "Customer ID", "Description", "Credited", "CGST", "SGST", "IGST", "Total paid", "Status", "Method"];
    const body = topupList.map(i => {
      const t = invoiceTotals(i);
      return [
        i.createdAt ? moment(i.createdAt).format("DD MMM YYYY, HH:mm") : "", invoiceNo(i), customerName(i), i.invoice_user_id?.user_unique_id || "",
        i.invoice_plan_name || "", t.sub.toFixed(2), t.cgst.toFixed(2), t.sgst.toFixed(2), t.igst.toFixed(2), t.tot.toFixed(2),
        invoiceStatus(i)[1], i.invoice_transaction_id?.method || "",
      ];
    });
    const csv = [head, ...body].map(r => r.map(x => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = one ? `wallet-statement-${one.user_unique_id || one._id}.csv` : `wallet-topups-${moment().format("YYYY-MM-DD")}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${topupList.length} top-ups`);
  };

  const resetFilters = () => {
    setSearchText("");
    setStatusFilter("all");
    setMethodFilter("all");
    setSortOrder("newest");
    selectClient("all");
  };

  // Page numbers with gaps (1 … 4 5 6 … 20)
  const pageItems = (): (number | "gap")[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const out: (number | "gap")[] = [1];
    if (page > 3) out.push("gap");
    for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) out.push(p);
    if (page < totalPages - 2) out.push("gap");
    out.push(totalPages);
    return out;
  };

  const firstRow = totalTopups ? (page - 1) * pageSize + 1 : 0;
  const lastRow = Math.min(page * pageSize, totalTopups);
  // All time has no previous period, so no growth %
  const delta = (s: StatItem) => (period === "all" ? "All time" : s.growth);

  const statItems: StripItem[] = one ? [
    { label: "Wallet balance", value: inr2(one.user_wallet_balance || 0), icon: "ri-wallet-3-line", delta: "Current balance" },
    { label: "Credited", value: inrShort(stats.credited.value), icon: "ri-add-circle-line", delta: delta(stats.credited) },
    { label: "Collected (incl. GST)", value: inrShort(stats.collected.value), icon: "ri-money-rupee-circle-line", delta: delta(stats.collected) },
    { label: "Top-ups", value: stats.count.value, icon: "ri-file-list-3-line", delta: delta(stats.count) },
  ] : [
    { label: "Credited to wallets", value: inrShort(stats.credited.value), icon: "ri-wallet-3-line", delta: delta(stats.credited) },
    { label: "Collected (incl. GST)", value: inrShort(stats.collected.value), icon: "ri-money-rupee-circle-line", delta: delta(stats.collected) },
    { label: "GST on top-ups", value: inrShort(stats.gst.value), icon: "ri-percent-line", delta: delta(stats.gst) },
    { label: "Top-ups", value: stats.count.value, icon: "ri-add-circle-line", delta: delta(stats.count) },
    { label: "Customers", value: stats.customers.value, icon: "ri-group-line", delta: delta(stats.customers) },
  ];

  return (
    <div className="container-fluid">
      {isLoader && <Loader />}
      <div className="cx">
        <PageHeader
          crumb="Wallet"
          title="Wallet top-ups"
          sub="Every wallet recharge — amount credited to the wallet, GST and payment."
          actions={<>
            <PeriodFilter
              allTime
              value={period}
              onChange={(p) => { setPeriod(p); setPage(1); }}
              from={customFrom}
              to={customTo}
              onRangeChange={(f, t) => { setCustomFrom(f); setCustomTo(t); setPage(1); }}
            />
            <button type="button" className="cx-btn cx-btn-light" onClick={exportCsv}><i className="ri-download-2-line"></i>{one ? "Statement" : "Export"}</button>
          </>}
        />

        <StatStrip items={statItems} />

        <div className="cx-card">
          <div className="cx-toolbar">
            {/* <select className="cx-select" style={{ minWidth: 220 }} value={client} onChange={(e) => selectClient(e.target.value)}>
              <option value="all">All customers</option>
              {customers.map(c => <option key={c._id} value={c._id}>{customerLabel(c)} ({c.topups})</option>)}
            </select> */}
            <select className="cx-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="all">All status</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
            {/* <select className="cx-select" value={methodFilter} onChange={(e) => { setMethodFilter(e.target.value); setPage(1); }}>
              <option value="all">All payment methods</option>
              {methods.map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
            </select> */}
            <select className="cx-select" value={sortOrder} onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}>
              <option value="newest">Sort: Newest</option>
              <option value="oldest">Sort: Oldest</option>
            </select>
            <div className="sp"></div>
            <input className="cx-input" type="search" placeholder="Invoice no / customer / email…" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
            {one && (
              <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={() => navigate(`/customers-and-users/view-user/${one._id}`)}>
                <i className="ri-building-2-line"></i>Customer
              </button>
            )}
            <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={resetFilters}>
              <i className="ri-refresh-line"></i>Reset
            </button>
          </div>

          <div className="cx-tbl-wrap">
            <table className="cx-tbl">
              <thead>
                <tr>
                  <th>Date &amp; time</th>{!one && <th>Customer</th>}<th>Invoice no.</th><th>Description</th>
                  {/* <th>Payment</th> */}
                  <th>Status</th>
                  <th className="r">Credited</th><th className="r">GST</th><th className="r">Total paid</th><th className="r">Actions</th>
                </tr>
              </thead>
              <tbody>
                {topupList.length ? topupList.map(i => {
                  const t = invoiceTotals(i);
                  const [tone, statusLabel] = invoiceStatus(i);
                  const userId = i.invoice_user_id?._id;
                  return (
                    <tr key={i._id} className="click" onClick={() => setOpenInvoice(i)}>
                      <td className="fs12 muted" style={{ whiteSpace: "nowrap" }}>{i.createdAt ? moment(i.createdAt).format("DD MMM YYYY, HH:mm") : "—"}</td>
                      {!one && (
                        <td onClick={(e) => e.stopPropagation()}>
                          {userId ? (
                            <button type="button" className="cx-link" style={{ color: "#12131c", fontWeight: 600 }} onClick={() => selectClient(userId)}>{customerName(i)}</button>
                          ) : <b style={{ fontWeight: 600 }}>{customerName(i)}</b>}
                          {i.invoice_user_id?.user_unique_id && <div className="fs12 muted mono">#{i.invoice_user_id.user_unique_id}</div>}
                        </td>
                      )}
                      <td className="mono fs12 fw6">{invoiceNo(i)}</td>
                      <td className="fs12">{i.invoice_plan_name || "Wallet top-up"}</td>
                      {/* <td className="fs12" style={{ textTransform: "uppercase" }}>{i.invoice_transaction_id?.method || <span className="muted">—</span>}</td> */}
                      <td><Pill tone={tone}>{statusLabel}</Pill></td>
                      <td className="mono up r">+{inr2(t.sub)}</td>
                      <td className="mono muted r">{inr2(t.gst)}</td>
                      <td className="mono fw6 r">{inr2(t.tot)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="cx-ra">
                          <button type="button" className="v" title="View invoice" onClick={() => setOpenInvoice(i)}><i className="ri-eye-line"></i></button>
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan={one ? 9 : 10}>
                      <div className="cx-empty">
                        <i className="ri-wallet-3-line"></i>
                        No wallet top-ups match these filters. <button type="button" className="cx-link" onClick={resetFilters}>Reset filters</button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="cx-tfoot">
            <span>Showing <b>{firstRow}–{lastRow}</b> of <b>{(totalTopups || 0).toLocaleString("en-IN")}</b> top-ups</span>
            <span>Credited = invoice taxable value (GST is on the invoice)</span>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <span>Rows</span>
              <select className="cx-select" value={pageSize} onChange={(e) => { setPageSize(+e.target.value); setPage(1); }}>
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <div className="cx-pager">
                <button type="button" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹</button>
                {pageItems().map((p, idx) =>
                  p === "gap" ? (
                    <span key={`gap-${idx}`} className="gap">…</span>
                  ) : (
                    <button type="button" key={p} className={p === page ? "on" : ""} onClick={() => setPage(p)}>{p}</button>
                  )
                )}
                <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>›</button>
              </div>
            </div>
          </div>
        </div>

        <InvoiceDrawer invoice={openInvoice} onClose={() => setOpenInvoice(null)} />
      </div>
    </div>
  );
};

export default Wallet;
